import * as PIXI from 'pixi.js';
import type { MapEnvironment } from '@machina/core';

/**
 * Fire Shader — Domain-warped fBm volumetric fire
 *
 * Shared by EffectsLayer (spell area effects) and IsoTokenRenderer (torch objects).
 * Inspired by THREE.Fire and Inigo Quilez's "Warping" article.
 * Double-warped noise creates organic flowing patterns; upward scroll +
 * fire color ramp give convincing flame effect. Additive blending for glow.
 *
 * Supports skinnable flame color ramps via 6-color uniform array,
 * and a teardrop shape mask (uShape=4) for torch flames.
 */

// ─── Flame Skins — keyed by environment category ────────────────────────────

export interface FlameSkin {
  /** 6 colors in the ramp: black, ember, red, orange, yellow, white */
  ramp: [number, number, number][];
  /** Glow tint for the Graphics fallback outer glow */
  glowColor: number;
  /** Outer flame fill for Graphics fallback */
  outerColor: number;
  /** Inner flame fill for Graphics fallback */
  innerColor: number;
  /** Core (white-hot center) */
  coreColor: number;
  /** Torch stick tint */
  stickColor: number;
  /** Bracket/iron color */
  bracketColor: number;
  /** Glass orb tint (low alpha overlay) */
  orbTint: number;
}

const SKIN_DEFAULT: FlameSkin = {
  ramp: [[0.01,0,0],[0.35,0.05,0],[0.75,0.12,0],[1,0.38,0.02],[1,0.72,0.12],[1,0.95,0.75]],
  glowColor: 0xff6600, outerColor: 0xff8c1a, innerColor: 0xffcc00,
  coreColor: 0xffffee, stickColor: 0x5c3a1e, bracketColor: 0x8b7355, orbTint: 0xffaa44,
};

const SKIN_CRYPT: FlameSkin = {
  ramp: [[0,0.02,0.01],[0,0.15,0.08],[0.05,0.35,0.15],[0.1,0.55,0.3],[0.3,0.8,0.5],[0.7,1,0.85]],
  glowColor: 0x00cc66, outerColor: 0x1aff8c, innerColor: 0x66ffcc,
  coreColor: 0xeeffee, stickColor: 0x3a3a3a, bracketColor: 0x555555, orbTint: 0x44ffaa,
};

const SKIN_LAVA: FlameSkin = {
  ramp: [[0.02,0,0],[0.4,0.02,0],[0.8,0.08,0],[1,0.2,0.02],[1,0.5,0.08],[1,0.85,0.6]],
  glowColor: 0xff2200, outerColor: 0xff4411, innerColor: 0xff8833,
  coreColor: 0xffddaa, stickColor: 0x2a1a0a, bracketColor: 0x664422, orbTint: 0xff6633,
};

const SKIN_ICE: FlameSkin = {
  ramp: [[0,0,0.02],[0.02,0.05,0.3],[0.05,0.15,0.6],[0.15,0.35,0.85],[0.4,0.65,1],[0.8,0.92,1]],
  glowColor: 0x3366ff, outerColor: 0x4488ff, innerColor: 0x88ccff,
  coreColor: 0xeef4ff, stickColor: 0x4a5568, bracketColor: 0x718096, orbTint: 0x66bbff,
};

const SKIN_FOREST: FlameSkin = {
  ramp: [[0.01,0.01,0],[0.25,0.12,0],[0.6,0.25,0],[0.9,0.45,0.05],[0.95,0.7,0.15],[1,0.95,0.7]],
  glowColor: 0xcc8800, outerColor: 0xddaa22, innerColor: 0xeedd44,
  coreColor: 0xffffdd, stickColor: 0x3d2b1f, bracketColor: 0x6b5b3e, orbTint: 0xddcc44,
};

const SKIN_TEMPLE: FlameSkin = {
  ramp: [[0.02,0.01,0],[0.3,0.15,0],[0.65,0.35,0.02],[0.9,0.6,0.08],[1,0.85,0.3],[1,1,0.9]],
  glowColor: 0xffaa00, outerColor: 0xffcc33, innerColor: 0xffee66,
  coreColor: 0xffffff, stickColor: 0x4a3520, bracketColor: 0xd4af37, orbTint: 0xffdd88,
};

const SKIN_SWAMP: FlameSkin = {
  ramp: [[0,0.01,0],[0.12,0.15,0],[0.3,0.4,0.02],[0.55,0.65,0.08],[0.75,0.85,0.2],[0.9,1,0.65]],
  glowColor: 0x88aa00, outerColor: 0xaacc22, innerColor: 0xccee44,
  coreColor: 0xeeffcc, stickColor: 0x3a3a20, bracketColor: 0x5a5a3a, orbTint: 0xaaee44,
};

const SKIN_URBAN: FlameSkin = {
  ramp: [[0.01,0,0],[0.3,0.08,0],[0.7,0.2,0.02],[1,0.45,0.05],[1,0.75,0.2],[1,0.95,0.8]],
  glowColor: 0xff9933, outerColor: 0xffaa44, innerColor: 0xffdd77,
  coreColor: 0xfffff0, stickColor: 0x4a3a2a, bracketColor: 0x888888, orbTint: 0xffcc77,
};

/** Map environment prefix → flame skin */
const SKIN_MAP: Record<string, FlameSkin> = {
  dungeon_crypt: SKIN_CRYPT,
  dungeon_temple: SKIN_TEMPLE,
  cave_lava: SKIN_LAVA,
  cave_ice: SKIN_ICE,
  cave_crystal: SKIN_ICE,
  forest_clearing: SKIN_FOREST,
  forest_dense: SKIN_FOREST,
  swamp: SKIN_SWAMP,
  urban_market: SKIN_URBAN,
  urban_alley: SKIN_URBAN,
  urban_docks: SKIN_URBAN,
  urban_noble: SKIN_URBAN,
  tavern: SKIN_URBAN,
};

/** Get the flame skin for a given map environment */
export function getFlameSkin(env?: MapEnvironment | string): FlameSkin {
  if (!env) return SKIN_DEFAULT;
  if (SKIN_MAP[env]) return SKIN_MAP[env];
  // Try prefix match (e.g. 'dungeon_stone' → dungeon default)
  const prefix = env.split('_')[0];
  if (prefix === 'cave') return SKIN_DEFAULT; // default fire for generic caves
  if (prefix === 'dungeon') return SKIN_DEFAULT;
  if (prefix === 'forest') return SKIN_FOREST;
  if (prefix === 'urban') return SKIN_URBAN;
  return SKIN_DEFAULT;
}

// ─── Shader source ──────────────────────────────────────────────────────────

export const FIRE_VERT = `
attribute vec2 aVertexPosition;
attribute vec2 aUvs;
uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;
varying vec2 vUvs;

void main() {
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    vUvs = aUvs;
}
`;

export const FIRE_FRAG = `
precision highp float;
varying vec2 vUvs;

uniform float uTime;
uniform float uIntensity;
uniform vec2 uOriginUV;   // fire center in UV space
uniform vec2 uScale;      // UV-to-radius scale factor
uniform float uShape;     // 0=circle, 1=square, 2=cone, 3=line, 4=teardrop
uniform float uLineHalf;  // half-width of line shape in radius units

// Skinnable color ramp (6 vec3s packed as 3 vec4s for compatibility)
uniform vec3 uRamp0; // black
uniform vec3 uRamp1; // ember
uniform vec3 uRamp2; // red
uniform vec3 uRamp3; // orange
uniform vec3 uRamp4; // yellow
uniform vec3 uRamp5; // white

// ─── Hash-based value noise ───────────────────────────────
float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract((p.x + p.y) * p.x);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}

// ─── fBm with octave rotation to break grid artifacts ─────
float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 5; i++) {
        sum += amp * noise(p);
        p = rot * p * 2.0;
        amp *= 0.5;
    }
    return sum;
}

// ─── Skinnable fire color ramp ───────────────────────────
vec3 fireColor(float t) {
    if (t < 0.15) return mix(uRamp0, uRamp1, t / 0.15);
    if (t < 0.30) return mix(uRamp1, uRamp2, (t - 0.15) / 0.15);
    if (t < 0.50) return mix(uRamp2, uRamp3, (t - 0.30) / 0.20);
    if (t < 0.70) return mix(uRamp3, uRamp4, (t - 0.50) / 0.20);
    return mix(uRamp4, uRamp5, clamp((t - 0.70) / 0.30, 0.0, 1.0));
}

void main() {
    vec2 local = (vUvs - uOriginUV) * uScale;

    // Shape mask — soft edge fade
    float dist = length(local);
    float mask;
    if (uShape < 0.5) {
        // Circle
        mask = 1.0 - smoothstep(0.75, 1.05, dist);
    } else if (uShape < 1.5) {
        // Square
        vec2 ap = abs(local);
        float sq = max(ap.x, ap.y);
        mask = 1.0 - smoothstep(0.75, 1.05, sq);
    } else if (uShape < 2.5) {
        // Cone
        float angle = atan(local.y, local.x);
        float coneHalf = 0.5236;
        float angleFade = 1.0 - smoothstep(coneHalf * 0.8, coneHalf * 1.1, abs(angle));
        float distFade = 1.0 - smoothstep(0.75, 1.05, dist);
        float tipFade = smoothstep(0.0, 0.12, dist);
        mask = angleFade * distFade * tipFade;
    } else if (uShape < 3.5) {
        // Line
        float xFade = (1.0 - smoothstep(0.75, 1.05, local.x)) * smoothstep(-0.05, 0.1, local.x);
        float yFade = 1.0 - smoothstep(uLineHalf * 0.7, uLineHalf * 1.1, abs(local.y));
        mask = xFade * yFade;
    } else {
        // Teardrop (shape=4) — flame emanating from narrow base, expanding upward
        // local.y < 0 = above origin (upward), local.y > 0 = below
        // Narrow at fuel point (h=0), widens to belly (~35% up), tapers to tip
        float h = -local.y; // h > 0 is upward into the flame
        float yFade = smoothstep(-0.08, 0.02, h) * (1.0 - smoothstep(0.7, 0.95, h));
        // Teardrop width: narrow base, belly at ~28%, sharp taper to pointed tip
        float t = clamp(h / 0.85, 0.0, 1.0); // compress into 85% of height (sharper tip)
        float teardrop = 0.07 + 0.35 * sin(t * 2.0); // 30% smaller, peaks at t≈0.28
        teardrop *= (1.0 - t * t * 0.7); // stronger taper → sharper point
        float xFade = 1.0 - smoothstep(teardrop * 0.75, teardrop * 1.1, abs(local.x));
        mask = xFade * yFade;
    }
    if (mask < 0.001) { gl_FragColor = vec4(0.0); return; }

    // ─── Domain-warped fBm ──────────────────────────────
    // For teardrop shape: use local.y directly (negative = up in screen space)
    // and scroll noise in -Y direction so patterns move upward on screen
    vec2 p;
    if (uShape > 3.5) {
        // Stretch vertical, keep Y orientation (negative = upward on screen)
        p = vec2(local.x * 1.8, local.y * 2.5);
    } else {
        p = local * vec2(1.5, 2.0);
    }

    // Torch flames: +uTime scrolls noise upward on screen
    // Spell effects: -uTime for the original molten look
    float s = uShape > 3.5 ? 1.0 : -0.55;

    vec2 q = vec2(
        fbm(p + vec2(0.0, uTime * s)),
        fbm(p + vec2(5.2, uTime * s * 1.2))
    );

    vec2 r = vec2(
        fbm(p + 4.0 * q + vec2(1.7, uTime * s * 0.7)),
        fbm(p + 4.0 * q + vec2(8.3, uTime * s * 0.85))
    );

    float n = fbm(p + 3.5 * r + vec2(0.0, uTime * s * 1.8));

    // ─── Spatial intensity shaping ───────────────────────
    float centerHeat;
    float dirBias;
    if (uShape > 3.5) {
        // For teardrop flame: hottest at base, trails off at tip
        // but noise patterns should visually lick upward
        float h = clamp(-local.y / 1.0, 0.0, 1.0);
        centerHeat = 1.0 - h * 0.3; // less falloff so upper parts stay visible
        dirBias = 1.0 + local.y * 0.2; // local.y < 0 = up = brighter (flames lick up)
    } else {
        centerHeat = 1.0 - dist * 0.35;
        dirBias = 1.0 + local.y * 0.12;
    }
    float pulse = 0.92 + 0.08 * sin(uTime * 2.3);

    float fireIntensity = n * centerHeat * dirBias * pulse * 1.35;
    fireIntensity = clamp(fireIntensity, 0.0, 1.0);

    // ─── Output ──────────────────────────────────────────
    vec3 color = fireColor(fireIntensity);
    float alpha = smoothstep(0.06, 0.28, fireIntensity) * mask * uIntensity;
    color *= 1.15;
    gl_FragColor = vec4(color * alpha, alpha);
}
`;

/**
 * Create a fire shader mesh for a circular area.
 * Used by spell area effects in the iso view.
 *
 * @param radiusPx - radius of the fire area in pixels
 * @param intensity - alpha/brightness (0-1)
 * @param skin - flame color skin (defaults to standard fire)
 * @returns mesh + shader (caller must update shader.uniforms.uTime each frame)
 */
export function createFireCircleMesh(
  radiusPx: number,
  intensity = 1.0,
  skin?: FlameSkin,
): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
  const s = skin ?? SKIN_DEFAULT;
  // Padding around the fire for flame wisps
  const extH = radiusPx * 1.3;
  const extUp = radiusPx * 1.8; // extra above for rising flames
  const extDown = radiusPx * 1.1;
  const totalW = extH * 2;
  const totalH = extUp + extDown;

  const originUV: [number, number] = [0.5, extUp / totalH];

  const geometry = new PIXI.Geometry()
    .addAttribute('aVertexPosition', new Float32Array([
      -extH, -extUp,
       extH, -extUp,
       extH,  extDown,
      -extH,  extDown,
    ]), 2)
    .addAttribute('aUvs', new Float32Array([
      0, 0,  1, 0,  1, 1,  0, 1,
    ]), 2)
    .addIndex([0, 1, 2, 0, 2, 3]);

  const shader = PIXI.Shader.from(FIRE_VERT, FIRE_FRAG, {
    uTime: 0.0,
    uIntensity: intensity,
    uOriginUV: originUV,
    uScale: [totalW / radiusPx, totalH / radiusPx],
    uShape: 0.0, // circle
    uLineHalf: 0.0,
    uRamp0: s.ramp[0], uRamp1: s.ramp[1], uRamp2: s.ramp[2],
    uRamp3: s.ramp[3], uRamp4: s.ramp[4], uRamp5: s.ramp[5],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mesh = new PIXI.Mesh(geometry, shader as any);
  mesh.blendMode = PIXI.BLEND_MODES.ADD;

  return { mesh, shader };
}

/**
 * Create a flame-shaped (teardrop) shader mesh for torch tokens.
 * Taller than wide, tapers to a point — proper flame tongue.
 *
 * @param heightPx - height of the flame in pixels
 * @param intensity - alpha/brightness (0-1)
 * @param skin - flame color skin
 * @returns mesh + shader (caller must update shader.uniforms.uTime each frame)
 */
export function createFlameMesh(
  heightPx: number,
  intensity = 1.0,
  skin?: FlameSkin,
): { mesh: PIXI.Mesh; shader: PIXI.Shader } {
  const s = skin ?? SKIN_DEFAULT;
  const halfW = heightPx * 0.55; // wide enough for pear belly
  const extUp = heightPx * 1.3;  // plenty of room for upward licking tips
  const extDown = heightPx * 0.2; // small skirt below base
  const totalW = halfW * 2;
  const totalH = extUp + extDown;

  // Origin at the base of the flame (bottom-center of the pear)
  // In UV space: origin is near the bottom of the quad
  const originUV: [number, number] = [0.5, extUp / totalH];

  const geometry = new PIXI.Geometry()
    .addAttribute('aVertexPosition', new Float32Array([
      -halfW, -extUp,
       halfW, -extUp,
       halfW,  extDown,
      -halfW,  extDown,
    ]), 2)
    .addAttribute('aUvs', new Float32Array([
      0, 0,  1, 0,  1, 1,  0, 1,
    ]), 2)
    .addIndex([0, 1, 2, 0, 2, 3]);

  const shader = PIXI.Shader.from(FIRE_VERT, FIRE_FRAG, {
    uTime: 0.0,
    uIntensity: intensity,
    uOriginUV: originUV,
    // Scale so pear width ≈ 0.55 maps to half the flame width, height maps 1:1
    uScale: [totalW / (heightPx * 0.5), totalH / heightPx],
    uShape: 4.0, // pear/teardrop
    uLineHalf: 0.0,
    uRamp0: s.ramp[0], uRamp1: s.ramp[1], uRamp2: s.ramp[2],
    uRamp3: s.ramp[3], uRamp4: s.ramp[4], uRamp5: s.ramp[5],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mesh = new PIXI.Mesh(geometry, shader as any);
  mesh.blendMode = PIXI.BLEND_MODES.ADD;

  return { mesh, shader };
}
